import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchProductByHandle } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { trackEvent } from "@/lib/metaPixel";
import { Loader2, Truck, Undo2, Award, ChevronRight, Minus, Plus, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { colorToHex } from "@/lib/colorMap";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Price } from "@/components/Price";
import { ProductReviews } from "@/components/ProductReviews";

export const Route = createFileRoute("/products/$handle")({
  loader: async ({ params }) => {
    try {
      const product = await fetchProductByHandle(params.handle);
      return { product };
    } catch {
      return { product: null };
    }
  },
  head: ({ params, loaderData }) => {
    const product = loaderData?.product as
      | { title?: string; description?: string; images?: { edges: Array<{ node: { url: string } }> } }
      | null
      | undefined;
    const prettySlug = params.handle
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const name = product?.title ?? prettySlug;
    const rawDesc = product?.description?.replace(/\s+/g, " ").trim() ?? "";
    const description =
      rawDesc.length >= 50
        ? rawDesc.slice(0, 158)
        : `Shop the ${name} — premium Texas-built lifestyle headwear from Coyote Creek Hat Co.`.slice(0, 158);
    const title = `${name} | Coyote Creek Hat Co.`;
    const url = `https://coyotecreekhatco.com/products/${params.handle}`;
    const image = product?.images?.edges?.[0]?.node?.url;

    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ProductPage,
});

interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  availableForSale: boolean;
  selectedOptions: Array<{ name: string; value: string }>;
}

function ProductPage() {
  const { handle } = Route.useParams();
  const addItem = useCartStore(s => s.addItem);
  const isCartLoading = useCartStore(s => s.isLoading);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const ctaRef = useRef<HTMLDivElement>(null);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", handle],
    queryFn: () => fetchProductByHandle(handle),
  });

  // Sticky mobile CTA observer
  useEffect(() => {
    const node = ctaRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [product]);

  const variants: Array<{ node: ShopifyVariant }> = product?.variants?.edges ?? [];
  const selectedVariant = variants[selectedVariantIdx]?.node;
  const images = product?.images?.edges ?? [];

  // Track ViewContent for Meta Pixel when product loads
  useEffect(() => {
    if (!product) return;
    trackEvent("ViewContent", {
      content_ids: [product.id],
      content_name: product.title,
      content_type: "product",
      value: parseFloat(product.priceRange.minVariantPrice.amount),
      currency: product.priceRange.minVariantPrice.currencyCode,
    });
  }, [product]);

  // Inject Product JSON-LD for SEO
  useEffect(() => {
    if (!product) return;
    const jsonLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.title,
      description: product.description,
      image: images.map((i: { node: { url: string } }) => i.node.url),
      brand: { "@type": "Brand", name: "Coyote Creek Hat Co." },
      offers: {
        "@type": "Offer",
        priceCurrency: product.priceRange.minVariantPrice.currencyCode,
        price: product.priceRange.minVariantPrice.amount,
        availability: variants.some((v) => v.node.availableForSale)
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    script.id = "product-jsonld";
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("product-jsonld");
      if (el) el.remove();
    };
  }, [product, images, variants]);

  // Determine the related collection tag
  const productTag = useMemo(() => {
    if (!product?.tags) return undefined;
    const known = ["Field", "Western", "Lifestyle"];
    return known.find((k) => product.tags.includes(k));
  }, [product]);

  // Hats-only sections (trust row + detail accordions)
  const isHat = useMemo(() => {
    const type = (product?.productType ?? "").toLowerCase();
    const tags: string[] = (product?.tags ?? []).map((t: string) => t.toLowerCase());
    const nonHat = ["shirt", "tee", "t-shirt", "top", "tops", "apparel"];
    if (nonHat.some((k) => type.includes(k) || tags.includes(k))) return false;
    return type.includes("hat") || type.includes("cap") || tags.some((t) => t.includes("hat") || t.includes("cap"));
  }, [product]);

  // Sanitize remote product HTML before rendering
  const sanitizedDescription = useMemo(() => {
    const raw = product?.descriptionHtml || product?.description || "";
    if (!raw) return "";
    if (typeof window === "undefined") return "";
    return DOMPurify.sanitize(raw);
  }, [product]);


  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#8B6F47]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="font-ui text-[#8B6F47]">Product not found</p>
      </div>
    );
  }

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    await addItem({
      product: { node: product },
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: quantity,
      selectedOptions: selectedVariant.selectedOptions || [],
    });
    toast.success(`${product.title} added to cart`, { position: "bottom-right" });
  };

  return (
    <div className="bg-[#F5EFE2]">
      {/* Breadcrumbs */}
      <div className="border-b border-[#D4CCBC] bg-[#FDFAF3]">
        <div className="mx-auto max-w-[1400px] px-6 py-3">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 font-ui text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8B6F47]">
            <Link to="/" className="hover:text-[#FA5021]">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/collections/$collection" params={{ collection: "all-hats" }} className="hover:text-[#FA5021]">Hats</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#2A1510] line-clamp-1">{product.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-10 lg:py-14">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16">
          {/* Gallery */}
          <div className="min-w-0">
            <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
              {/* Thumbnails (vertical desktop) */}
              {images.length > 1 && (
                <div className="order-2 flex max-w-full min-w-0 gap-3 overflow-x-auto lg:order-1 lg:flex-col lg:overflow-visible">
                  {images.map((img: { node: { url: string; altText: string | null } }, i: number) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm border-2 transition-all ${i === selectedImage ? "border-[#FA5021]" : "border-[#D4CCBC] opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img.node.url} alt={img.node.altText || ""} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Main image */}
              <div className="order-1 min-w-0 flex-1 lg:order-2">
                <button
                  onClick={() => setZoomOpen(true)}
                  className="group relative block h-[min(82vw,52vh)] min-h-[300px] w-full max-w-full overflow-hidden rounded-sm border border-[#D4CCBC] bg-[#FDFAF3] sm:aspect-square sm:h-auto"
                >
                  {images[selectedImage]?.node ? (
                    <img
                      src={images[selectedImage].node.url}
                      alt={images[selectedImage].node.altText || product.title}
                      className="h-full w-full object-contain p-5 transition-transform duration-500 group-hover:scale-105 sm:p-0"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[#8B6F47]">No image</div>
                  )}
                  <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#FDFAF3]/90 text-[#2A1510] opacity-0 transition-opacity group-hover:opacity-100">
                    <ZoomIn className="h-4 w-4" />
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Details (sticky on desktop) */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="font-ui text-[11px] font-bold uppercase tracking-[0.25em] text-saddle">
              {product.productType || "Premium Hat"}
            </p>
            <h1 className="mt-2 font-heading text-3xl font-bold uppercase leading-tight text-[#2A1510] md:text-5xl">
              {product.title}
            </h1>
            <p className="mt-3 font-body text-3xl font-bold text-[#8B6F47]">
              <Price amount={selectedVariant ? selectedVariant.price.amount : product.priceRange.minVariantPrice.amount} />
            </p>

            <div
              className="prose-description mt-5 font-body text-base leading-relaxed text-[#2A1510]/80"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />


            {/* Variant pickers */}
            <div ref={ctaRef} className="mt-7 space-y-5">
              {product.options?.filter((option: { name: string; values: string[] }) => !(option.name.toLowerCase() === "title" && option.values.length === 1 && option.values[0].toLowerCase() === "default title")).map((option: { name: string; values: string[] }) => {
                const isColor = option.name.toLowerCase() === "color";
                const isSize = option.name.toLowerCase() === "size";
                const currentValue = selectedVariant?.selectedOptions.find(o => o.name === option.name)?.value;
                return (
                  <div key={option.name}>
                    <label className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">
                      {option.name}: <span className="text-[#8B6F47]">{currentValue}</span>
                    </label>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {(isSize
                        ? [...option.values].sort((a, b) => {
                            const order = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL"];
                            const ai = order.indexOf(a.toUpperCase());
                            const bi = order.indexOf(b.toUpperCase());
                            if (ai === -1 && bi === -1) return a.localeCompare(b);
                            if (ai === -1) return 1;
                            if (bi === -1) return -1;
                            return ai - bi;
                          })
                        : option.values
                      ).map((value: string) => {
                        const variantIdx = variants.findIndex(v => v.node.selectedOptions.some(o => o.name === option.name && o.value === value));
                        const isSelected = variantIdx === selectedVariantIdx;
                        const isAvailable = variantIdx >= 0 && variants[variantIdx].node.availableForSale;
                        if (isColor) {
                          return (
                            <button
                              key={value}
                              onClick={() => variantIdx >= 0 && setSelectedVariantIdx(variantIdx)}
                              title={value}
                              className={`h-9 w-9 rounded-full border-2 transition-all ${isSelected ? "border-[#FA5021] ring-2 ring-[#FA5021]/30 ring-offset-2 ring-offset-[#F5EFE2]" : "border-[#D4CCBC]"} ${!isAvailable ? "opacity-40" : ""}`}
                              style={{ backgroundColor: colorToHex(value) }}
                            />
                          );
                        }
                        return (
                          <button
                            key={value}
                            onClick={() => variantIdx >= 0 && setSelectedVariantIdx(variantIdx)}
                            className={`min-w-[52px] rounded-sm border px-4 py-2 font-ui text-xs font-semibold uppercase tracking-wider transition-all ${isSelected ? "border-[#FA5021] bg-[#FA5021]/10 text-[#2A1510]" : "border-[#D4CCBC] text-[#2A1510] hover:border-[#2A1510]"} ${!isAvailable ? "opacity-40 line-through" : ""} ${isSize ? "" : ""}`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quantity + CTA */}
            <div className="mt-7 flex items-stretch gap-3">
              <div className="flex items-center rounded-sm border border-[#D4CCBC] bg-[#FDFAF3]">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="flex h-12 w-10 items-center justify-center text-[#2A1510] hover:bg-[#F5EFE2]"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-ui text-sm font-bold text-[#2A1510]">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="flex h-12 w-10 items-center justify-center text-[#2A1510] hover:bg-[#F5EFE2]"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={isCartLoading || !selectedVariant?.availableForSale}
                className="flex-1 btn-cc-accent tracking-[0.2em]"
              >
                {isCartLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : selectedVariant?.availableForSale ? "Add to Cart" : "Sold Out"}
              </button>
            </div>

            {/* Trust row + details — hats only */}
            {isHat && (
            <>
            <div className="mt-6 grid grid-cols-3 gap-3 border-y border-[#D4CCBC] py-5 text-center">

              <div className="flex flex-col items-center gap-1.5">
                <Truck className="h-5 w-5 text-[#FA5021]" />
                <span className="font-ui text-[10px] font-bold uppercase tracking-[0.15em] text-[#2A1510]">Free Ship $75+</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <Undo2 className="h-5 w-5 text-[#FA5021]" />
                <span className="font-ui text-[10px] font-bold uppercase tracking-[0.15em] text-[#2A1510]">30-Day Returns</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <Award className="h-5 w-5 text-[#FA5021]" />
                <span className="font-ui text-[10px] font-bold uppercase tracking-[0.15em] text-[#2A1510]">Dadgum Best</span>
              </div>
            </div>

            {/* Accordions */}
            <Accordion type="multiple" className="mt-2">
              <AccordionItem value="fit" className="border-b border-[#D4CCBC]">
                <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#2A1510] hover:no-underline">
                  Fit & Sizing
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-[#2A1510]/80">
                  Most styles run true to size. Adjustable snapback for a custom fit. Need help? Reach out via our Contact page and we'll get you sorted.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care" className="border-b border-[#D4CCBC]">
                <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#2A1510] hover:no-underline">
                  Care Instructions
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-[#2A1510]/80">
                  Spot clean with a damp cloth. Avoid machine washing. Reshape and air dry away from direct sunlight to keep it looking like the day you got it.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping" className="border-b border-[#D4CCBC]">
                <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#2A1510] hover:no-underline">
                  Shipping & Returns
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-[#2A1510]/80">
                  Free U.S. shipping on orders over $75. Standard delivery in 3–5 business days. Easy 30-day returns — see our policies for the details.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            </>
            )}

          </div>
        </div>

        <ProductReviews
          handle={product.handle}
          productTitle={product.title}
          shopifyProductId={Number(String(product.id).split('/').pop())}
        />
      </div>

      {/* Related */}
      <RelatedProducts tag={productTag} excludeHandle={product.handle} />

      {/* Sticky mobile CTA */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#D4CCBC] bg-[#FDFAF3] px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] transition-transform duration-300 lg:hidden ${stickyVisible ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-heading text-sm font-bold uppercase text-[#2A1510] line-clamp-1">{product.title}</p>
            <p className="font-body text-sm font-bold text-[#8B6F47]">
              <Price amount={selectedVariant ? selectedVariant.price.amount : product.priceRange.minVariantPrice.amount} />
            </p>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={isCartLoading || !selectedVariant?.availableForSale}
            className="btn-cc-accent tracking-[0.2em]"
          >
            {isCartLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : selectedVariant?.availableForSale ? "Add" : "Sold Out"}
          </button>
        </div>
      </div>

      {/* Zoom dialog */}
      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] max-w-5xl overflow-hidden bg-[#FDFAF3] p-2">
          <DialogTitle className="sr-only">{product.title} — full size image</DialogTitle>
          {images[selectedImage]?.node && (
            <img
              src={images[selectedImage].node.url}
              alt={images[selectedImage].node.altText || product.title}
              className="max-h-[calc(100dvh-4rem)] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
